import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import DarkModeToggle from '../DarkModeToggle';

interface NavLink {
    href: string;
    label: string;
    num: string;
}

export default function Navbar(): React.ReactElement {
    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const [activeSection, setActiveSection] = useState<string>('');

    const navLinks: NavLink[] = [
        { href: '#about', label: 'About', num: '01' },
        { href: '#experience', label: 'Experience', num: '02' },
        { href: '#projects', label: 'Projects', num: '03' },
        { href: '#contact', label: 'Contact', num: '04' },
    ];

    const handleScroll = useCallback(() => {
        setIsScrolled(window.scrollY > 50);

        const sections = ['contact', 'projects', 'experience', 'about'];
        for (const section of sections) {
            const el = document.getElementById(section);
            if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.top <= 200) {
                    setActiveSection(`#${section}`);
                    return;
                }
            }
        }
        setActiveSection('');
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    }, [isMobileMenuOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isMobileMenuOpen) setIsMobileMenuOpen(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isMobileMenuOpen]);

    return (
        <>
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                className={`nav-container ${isScrolled ? 'nav-scrolled' : 'pt-4'}`}
            >
                <div className="container-main flex items-center justify-between py-6">
                    <a href="/" className="flex items-center gap-1 group">
                        <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                            gouravraj
                        </span>
                        <span className="text-xl font-bold text-gradient">.com</span>
                    </a>

                    <div className="hidden md:flex items-center gap-1">
                        {navLinks.map((link, i) => (
                            <motion.a
                                key={link.href}
                                href={link.href}
                                initial={{ y: -20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                                className={`nav-link ${activeSection === link.href ? 'active' : ''}`}
                            >
                                <span className="nav-number mr-1">{link.num}.</span>
                                {link.label}
                            </motion.a>
                        ))}
                        <motion.a
                            href="/resume.pdf"
                            target="_blank"
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.4 }}
                            className="btn-secondary ml-3 !py-1.5 !px-3.5 text-sm"
                        >
                            <FontAwesomeIcon icon={faFileAlt} />
                            <span>Résumé</span>
                        </motion.a>
                        <motion.div
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.58, duration: 0.4 }}
                            className="ml-2"
                        >
                            <DarkModeToggle />
                        </motion.div>
                    </div>

                    <div className="md:hidden flex items-center gap-2 z-[100]">
                        <DarkModeToggle />
                    <button
                        className="p-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <div className="flex flex-col gap-1.5 w-6">
                            <motion.span
                                animate={isMobileMenuOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
                                className="block h-0.5 w-full bg-[var(--color-text-primary)] rounded"
                            />
                            <motion.span
                                animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                                className="block h-0.5 w-full bg-[var(--color-text-primary)] rounded"
                            />
                            <motion.span
                                animate={isMobileMenuOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
                                className="block h-0.5 w-full bg-[var(--color-text-primary)] rounded"
                            />
                        </div>
                    </button>
                    </div>
                </div>
            </motion.nav>

            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mobile-menu open"
                    >
                        {navLinks.map((link, i) => (
                            <motion.a
                                key={link.href}
                                href={link.href}
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.1 + i * 0.08 }}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <span className="nav-number text-base mr-2">{link.num}.</span>
                                {link.label}
                            </motion.a>
                        ))}
                        <motion.a
                            href="/resume.pdf"
                            target="_blank"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="btn-primary mt-4"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            <FontAwesomeIcon icon={faFileAlt} />
                            <span>View Résumé</span>
                        </motion.a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
